import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { isBirthCheckOk } from 'validators/Validator';
import { FormErrors } from 'validators/RegisterMemberValidator';
import { callCheckGakkenIdDuplicateApi } from 'requests/RequestUser';
import { RegisterMemberForm, MemberInputDto } from 'components/templates/MemberInputTemplate';
import { formatDate, DATE } from 'common/dateFormatter';
import { RoutePath } from 'consts/RoutePath';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import {
	RegisterGroupMemberReqData,
	callRegisterGroupMemberApi,
	GroupType,
	callRegisterGroupApi,
} from 'requests/RequestGroup';
import { CONFIRM_ERROR_PASSWORD, HARD_TO_GUESS_PASSWORD } from 'utils/messages';

/**
 * メンバー登録処理のカスタムフック
 * フォームバリデーション -> 更新実処理 -> ページ遷移 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */

export const useRegisterMember = (
	formErrors: FormErrors,
	setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
	getErrorMessage: Function,
	actionError: string,
	setActionError: React.Dispatch<React.SetStateAction<string>>,
	memberFormData: RegisterMemberForm,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
	setPage: React.Dispatch<React.SetStateAction<number>>,
	dto?: MemberInputDto,
	fromPage?: string,
) => {
	// フォームバリデーション
	const [isFormValidationFinished, setIsFormValidationFinished] = useState(false);
	const validate = async () => {
		setProcessLoading(true);

		Object.keys(formErrors).forEach((k) => {
			setFormErrors((prev) => ({ ...prev, [k]: getErrorMessage(k, memberFormData[k]) }));
		});
		const isPasswordsMatched = memberFormData.pass === memberFormData.password_confirm;
		const hardToGuessPassword = memberFormData.gid.toLowerCase() !== memberFormData.pass.toLowerCase();

		if (!isPasswordsMatched) {
			setFormErrors((prev) => ({
				...prev,
				password_confirm: CONFIRM_ERROR_PASSWORD,
			}));
		}
		// GIDと同じパスワードを設定できない
		if (!hardToGuessPassword) {
			setFormErrors((prev) => ({
				...prev,
				pass: HARD_TO_GUESS_PASSWORD,
			}));
		}
		// 年月日どれかが入力されている場合、生年月日の正当性チェック
		if (memberFormData.birthYear || memberFormData.birthMonth || memberFormData.birthDate) {
			const isOk = isBirthCheckOk(memberFormData.birthYear, memberFormData.birthMonth, memberFormData.birthDate);
			if (!isOk) {
				setFormErrors((prev) => ({ ...prev, birthYear: '正しい生年月日を入力してください。' }));
			}
		}
		setIsFormValidationFinished(true);
	};

	// 学研ID重複確認
	const history = useHistory();
	const [isGidDuplicateValidationFinished, setIsGidDuplicateValidationFinished] = useState(false);
	useEffect(() => {
		if (!isFormValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const execute = async () => {
			setActionError('');
			const checkGidResult = await callCheckGakkenIdDuplicateApi({
				gid: memberFormData.gid,
			});
			if (checkGidResult.status !== HttpStatus.OK) {
				setActionError(ReactError.E9999);
				history.push(RoutePath.ERROR);
				return;
			}
			if (checkGidResult.data.exist) {
				setFormErrors((prev) => ({ ...prev, gid: ReactError.E0005 }));
			}
			setIsGidDuplicateValidationFinished(true);
		};
		execute();
	}, [isFormValidationFinished]);

	useEffect(() => {
		if (!isGidDuplicateValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsGidDuplicateValidationFinished(false);
			setProcessLoading(false);
			return;
		}
		setProcessLoading(false);
		setPage((prev) => prev + 1);
	}, [isGidDuplicateValidationFinished]);

	// 登録実処理
	const [isRegisterFinished, setIsRegisterFinished] = useState(false);
	const register = async () => {
		setProcessLoading(true);
		const isBirthExist = !!memberFormData.birthYear && !!memberFormData.birthMonth && !!memberFormData.birthDate;

		// グループが存在しなかったら登録
		let groupId = dto?.groupId;
		if (!groupId) {
			const registerGroupResult = await callRegisterGroupApi(GroupType.FAMILY);
			if (registerGroupResult.status !== HttpStatus.OK) {
				setIsRegisterFinished(true);
				history.push(RoutePath.ERROR);
				return;
			}
			groupId = registerGroupResult.data.group_id;
		}

		// メンバー情報登録
		const registerGroupMemberReqData: RegisterGroupMemberReqData = {
			gid: memberFormData.gid,
			pass: memberFormData.pass,
			lastname: memberFormData.lastname,
			firstname: memberFormData.firstname,
			kana_lastname: memberFormData.kana_lastname,
			kana_firstname: memberFormData.kana_firstname,
			birth: isBirthExist
				? formatDate(
						new Date(
							Number(memberFormData.birthYear),
							Number(memberFormData.birthMonth) - 1,
							Number(memberFormData.birthDate),
						),
						DATE,
				  )
				: undefined,
			sex: memberFormData.sex,
		};
		const result = await callRegisterGroupMemberApi(groupId, registerGroupMemberReqData);
		if (result.status !== HttpStatus.OK) {
			setIsRegisterFinished(true);
			history.push(RoutePath.ERROR);
			return;
		}
		setIsRegisterFinished(true);
	};

	// ページ遷移(登録完了時)
	useEffect(() => {
		if (!isRegisterFinished) {
			return;
		}

		const isError = actionError !== '';
		if (isError) {
			setIsRegisterFinished(false);
			setProcessLoading(false);
			return;
		}
		// memberAddから来た場合、memberAddに戻る
		if (fromPage == 'memberAdd') {
			history.push({ pathname: RoutePath.MEMBER_ADD, state: { isMemberRegistered: true } });
		} else {
			setPage((prev) => prev + 1);
		}
	}, [isRegisterFinished]);

	// 戻る
	const back = async () => {
		setIsFormValidationFinished(false);
		setIsGidDuplicateValidationFinished(false);
		setIsRegisterFinished(false);
		setProcessLoading(false);
		setPage((prev) => prev - 1);
	};

	return {
		validate,
		register,
		back,
	};
};
